import { useState, useEffect } from 'react';
import {
  getProject, updateProject,
  getPopulation, savePopulation,
  getSample, saveSample,
  getScores, saveScore,
  logSampleRun, getSampleRuns,
} from '../lib/supabase';

// Placeholder panels — these will be built out in subsequent phases
import UploadPanel from '../components/UploadPanel';
import SamplePanel from '../components/SamplePanel';
import ScoringPanel from '../components/ScoringPanel';
import ResultsPanel from '../components/ResultsPanel';

const STEPS = [
  { key: 'upload',   label: '01 Population',     status: 'draft' },
  { key: 'sample',   label: '02 Sample Plan',     status: 'sampled' },
  { key: 'pause',    label: '03 Client Review',   status: 'awaiting_scoring' },
  { key: 'score',    label: '04 Scoring',         status: 'awaiting_scoring' },
  { key: 'results',  label: '05 Results',         status: 'complete' },
];

// Determines the correct step based on status AND actual data presence
function resolveStep(status, hasPopulation, hasSample, hasAllScores) {
  if (status === 'complete') return 'results';
  if (status === 'awaiting_scoring') {
    // If scores are all entered go to results, otherwise pause screen
    return hasAllScores ? 'results' : 'pause';
  }
  if (status === 'sampled' || hasSample) return 'pause';
  if (hasPopulation) return 'sample';
  return 'upload';
}

export default function ProjectPage({ session, profile, project: initialEng, onBack, onLogout }) {
  const [project, setProject] = useState(initialEng);
  const [population, setPopulation] = useState(null);
  const [sampleData, setSampleData] = useState(null);
  const [scores, setScores] = useState({});
  const [activeStep, setActiveStep] = useState('upload'); // corrected after data loads
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sampleRuns, setSampleRuns] = useState([]);

  const teamId = profile?.team_id;
  const userId = session?.user?.id;

  // Load all existing data for this project on mount
  useEffect(() => {
    loadProjectData();
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const [popRes, sampleRes, scoresRes, engRes, runsRes] = await Promise.all([
        getPopulation(project.id),
        getSample(project.id),
        getScores(project.id),
        getProject(project.id),
        getSampleRuns(project.id),
      ]);

      const freshProject  = engRes.data || project;
      const freshPop      = popRes.data?.projects || null;
      const freshSample   = sampleRes.data || null;
      const freshScoreMap = {};
      if (scoresRes.data) {
        scoresRes.data.forEach(s => { freshScoreMap[s.project_id] = s.qualified_pct; });
      }

      const drawnProjs = freshSample?.drawn_projects || [];
      const allScored  = drawnProjs.length > 0 &&
        drawnProjs.every(p => freshScoreMap[String(p._id)] != null);

      const correctStep = resolveStep(
        freshProject.status,
        !!freshPop,
        !!freshSample,
        allScored
      );

      setProject(freshProject);
      setPopulation(freshPop);
      setSampleData(freshSample);
      setScores(freshScoreMap);
      setSampleRuns(runsRes.data || []);
      setActiveStep(correctStep);
    } catch (err) {
      console.error('Failed to load project data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers passed down to child panels ──────────────────

  const handlePopulationSaved = async (projects) => {
    setSaving(true);
    await savePopulation(project.id, teamId, projects);
    setPopulation(projects);
    setSaving(false);
    setActiveStep('sample');
  };

  const handleSampleDrawn = async (sample) => {
    setSaving(true);
    // Save sample to DB
    await saveSample(project.id, teamId, userId, {
      seed: sample.seed,
      strata: sample.strata,
      large100: sample.large100,
      drawn_projects: sample.drawnProjects,
      sampling_mode: sample.samplingMode || 'variable',
      attr_plan: sample.attrPlan || null,
    });
    // Update project status
    const { data: updated } = await updateProject(project.id, {
      status: 'awaiting_scoring',
      settings: sample.settings,
    });
    // Log this sample run
    await logSampleRun(project.id, teamId, userId, {
      seed: sample.seed,
      settings: sample.settings,
      total_sample: sample.drawnProjects.length,
      projected_rp: sample.projectedRP || null,
      strata_summary: sample.strata.map(st => ({
        id: st.id, n: st.n, Ni: st.Ni, label: st.label,
      })),
    });
    // Reload run log
    const { data: runs } = await getSampleRuns(project.id);
    setSampleRuns(runs || []);

    setSampleData({
      seed: sample.seed,
      strata: sample.strata,
      large100: sample.large100,
      drawn_projects: sample.drawnProjects,
      samplingMode: sample.samplingMode || 'variable',
      attrPlan: sample.attrPlan || null,
    });
    if (updated) setProject(updated);
    setSaving(false);
    setActiveStep('pause');
  };

  const handleResumeScoring = () => {
    setActiveStep('score');
  };

  const handleScoreSaved = async (projectId, qualifiedPct) => {
    await saveScore(project.id, teamId, userId, projectId, qualifiedPct);
    setScores(prev => ({ ...prev, [String(projectId)]: qualifiedPct }));
  };

  const handleScoringComplete = async () => {
    setSaving(true);
    const { data: updated } = await updateProject(project.id, { status: 'complete' });
    if (updated) setProject(updated);
    setSaving(false);
    setActiveStep('results');
  };

  // ── Step nav helpers ───────────────────────────────────────

  const stepIndex = (key) => STEPS.findIndex(s => s.key === key);
  const activeIdx = stepIndex(activeStep);

  const canNavigateTo = (key) => {
    const target = stepIndex(key);
    if (target <= activeIdx) return true;
    if (key === 'sample' && population) return true;
    if (key === 'pause' && sampleData) return true;
    if (key === 'score' && sampleData) return true;
    if (key === 'results' && (project.status === 'complete' || allScored)) return true;
    return false;
  };

  const exportSampleList = (projects, proj) => {
    const rows = [
      [`Sample Project List — ${proj.client_name}`],
      [`Tax Years: ${proj.tax_years || 'N/A'}`],
      [`Total Projects: ${projects.length}`],
      [],
      ['#', 'Project Name', 'Stratum', 'Reported Amount ($)', 'Tax Year'],
      ...projects.map((p, i) => [
        i + 1,
        p.name,
        p.isLarge ? '100% (Certainty)' : p.stratum,
        Math.round(p.amount),
        p.year || '',
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `sample_list_${proj.client_name.replace(/\s+/g, '_')}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.loadingText}>Loading project…</div>
      </div>
    );
  }

  const drawnProjects = sampleData?.drawn_projects || [];
  const scoreCount = drawnProjects.filter(p => scores[String(p._id)] != null).length;
  const allScored = drawnProjects.length > 0 && scoreCount === drawnProjects.length;

  return (
    <div style={styles.page}>
      {/* ── Nav ── */}
      <div style={styles.nav}>
        <div style={styles.navLeft}>
          <button style={styles.backBtn} onClick={() => onBack(project)}>← Dashboard</button>
          <div style={styles.navDivider} />
          <div>
            <div style={styles.navClient}>{project.client_name}</div>
            {project.tax_years && (
              <div style={styles.navYears}>Tax Years: {project.tax_years}</div>
            )}
          </div>
        </div>
        <div style={styles.navRight}>
          {saving && <div style={styles.savingBadge}>Saving…</div>}
          <div style={styles.navUser}>{profile?.full_name || session.user.email}</div>
        </div>
      </div>

      {/* ── Step bar ── */}
      <div style={styles.stepBar}>
        {STEPS.map((s, i) => {
          const isActive = s.key === activeStep;
          const isDone = i < activeIdx;
          const canNav = canNavigateTo(s.key);
          return (
            <button
              key={s.key}
              style={{
                ...styles.stepBtn,
                ...(isActive ? styles.stepActive : {}),
                ...(isDone ? styles.stepDone : {}),
                cursor: canNav ? 'pointer' : 'default',
                opacity: canNav ? 1 : 0.45,
              }}
              onClick={() => canNav && setActiveStep(s.key)}
              disabled={!canNav}
            >
              {isDone ? '✓ ' : ''}{s.label}
            </button>
          );
        })}
      </div>

      {/* ── Panel ── */}
      <div style={styles.body}>

        {activeStep === 'upload' && (
          <UploadPanel
            project={project}
            existingPopulation={population}
            onSave={handlePopulationSaved}
            saving={saving}
          />
        )}

        {activeStep === 'sample' && (
          <SamplePanel
            project={project}
            population={population}
            existingSample={sampleData}
            onDraw={handleSampleDrawn}
            saving={saving}
            profile={profile}
            onProjectUpdate={(updated) => setProject(updated)}
          />
        )}

        {activeStep === 'pause' && (
          <div style={styles.pauseCard}>
            <div style={styles.pauseTop}>
              <div>
                <div style={styles.pauseIcon}>⏸</div>
                <h2 style={styles.pauseTitle}>Sample Drawn — Awaiting Client Review</h2>
                <p style={styles.pauseText}>
                  The sample of <strong>{drawnProjects.length} projects</strong> has been drawn and saved.
                  You can close this browser and return at any time — your work is saved.
                </p>
                <p style={styles.pauseText}>
                  Download the project list below and share it with your client to schedule
                  qualification interviews. When you're ready to enter scores, click Resume.
                </p>
                <div style={styles.pauseInfo}>
                  {[
                    ['Random Seed', sampleData?.seed],
                    ['Projects to Score', drawnProjects.length],
                    ['Scores Entered', `${scoreCount} of ${drawnProjects.length}`],
                  ].map(([lbl, val]) => (
                    <div key={lbl} style={styles.pauseInfoRow}>
                      <span style={styles.pauseInfoLabel}>{lbl}</span>
                      <span style={styles.pauseInfoValue}>{val}</span>
                    </div>
                  ))}
                </div>
                <div style={styles.pauseBtns}>
                  <button style={styles.downloadBtn} onClick={() => exportSampleList(drawnProjects, project)}>
                    ⬇ Download Project List (CSV)
                  </button>
                  <button style={styles.resumeBtn} onClick={handleResumeScoring}>
                    Resume — Enter Scores →
                  </button>
                </div>
              </div>
            </div>

            {/* Sample list preview */}
            <div style={styles.pauseListWrap}>
              <div style={styles.pauseListTitle}>
                Sampled Projects — share this list with your client
              </div>
              <table style={styles.pauseTable}>
                <thead>
                  <tr>
                    {['#', 'Project Name', 'Stratum', 'Reported Amount', 'Tax Year'].map(h => (
                      <th key={h} style={styles.pauseTh}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drawnProjects.map((p, i) => (
                    <tr key={p._id} style={{ borderBottom: '1px solid #f0f2f8' }}>
                      <td style={styles.pauseTd}>{i + 1}</td>
                      <td style={{ ...styles.pauseTd, fontWeight: 600, color: '#0D2461', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</td>
                      <td style={styles.pauseTd}>
                        <span style={{
                          background: p.isLarge ? '#fff0ee' : '#e8ecf5',
                          color: p.isLarge ? '#c03a1a' : '#0D2461',
                          borderRadius: 3, padding: '2px 8px',
                          fontFamily: "'Roboto Mono', monospace", fontSize: 10, fontWeight: 600,
                        }}>
                          {p.isLarge ? '100%' : p.stratum}
                        </span>
                      </td>
                      <td style={{ ...styles.pauseTd, fontFamily: "'Roboto Mono', monospace" }}>
                        ${Math.round(p.amount).toLocaleString()}
                      </td>
                      <td style={{ ...styles.pauseTd, color: '#8a96b0' }}>{p.year || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeStep === 'score' && (
          <ScoringPanel
            project={project}
            drawnProjects={drawnProjects}
            scores={scores}
            onScoreSaved={handleScoreSaved}
            onComplete={handleScoringComplete}
            allScored={allScored}
            scoreCount={scoreCount}
            saving={saving}
          />
        )}

        {activeStep === 'results' && (
          <ResultsPanel
            project={project}
            population={population}
            sampleData={sampleData}
            scores={scores}
            profile={profile}
            sampleRuns={sampleRuns}
          />
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f4f6fb',
    fontFamily: "'Montserrat', sans-serif",
  },
  loadingPage: {
    minHeight: '100vh',
    background: '#0D2461',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '13px',
    color: 'rgba(255,255,255,0.5)',
  },
  nav: {
    height: 60,
    background: '#0D2461',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 28px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.6)',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    cursor: 'pointer',
    padding: 0,
  },
  navDivider: { width: 1, height: 28, background: 'rgba(255,255,255,0.15)' },
  navClient: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: '15px',
    color: 'white',
  },
  navYears: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px',
    color: 'rgba(255,255,255,0.45)',
    letterSpacing: '0.04em',
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '14px' },
  savingBadge: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#F5A800',
    letterSpacing: '0.04em',
  },
  navUser: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
  },
  stepBar: {
    display: 'flex',
    background: '#e8ecf5',
    borderBottom: '1.5px solid #c8d0e0',
  },
  stepBtn: {
    flex: 1,
    padding: '11px 8px',
    background: 'none',
    border: 'none',
    borderRight: '1.5px solid #c8d0e0',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#8a96b0',
    transition: 'all 0.15s',
  },
  stepActive: {
    background: '#0D2461',
    color: '#F5A800',
    fontWeight: 700,
  },
  stepDone: {
    background: '#F5A800',
    color: '#0D2461',
    fontWeight: 700,
  },
  body: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 24px 80px',
  },
  // ── Pause screen ──
  pauseCard: {
    background: 'white',
    border: '1.5px solid #c8d0e0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  pauseIcon: { fontSize: '40px', marginBottom: '12px' },
  pauseTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '22px',
    color: '#0D2461',
    margin: '0 0 16px',
  },
  pauseText: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    color: '#5a6a8a',
    lineHeight: 1.7,
    marginBottom: '12px',
  },
  pauseInfo: {
    background: '#f4f6fb',
    border: '1.5px solid #c8d0e0',
    borderRadius: '6px',
    padding: '16px 24px',
    margin: '20px 0 0',
    textAlign: 'left',
  },
  pauseInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: '1px solid #e8ecf5',
  },
  pauseInfoLabel: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#8a96b0',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  pauseInfoValue: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    fontWeight: 600,
    color: '#0D2461',
  },
  resumeBtn: {
    padding: '13px 28px',
    background: '#F5A800',
    color: '#0D2461',
    border: 'none',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '14px',
    cursor: 'pointer',
  },
  downloadBtn: {
    padding: '13px 28px',
    background: '#0D2461',
    color: '#F5A800',
    border: 'none',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer',
  },
  pauseBtns: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
    flexWrap: 'wrap',
  },
  pauseTop: {
    padding: '40px 40px 24px',
    borderBottom: '1.5px solid #e8ecf5',
  },
  pauseListWrap: {
    padding: '24px 40px 40px',
  },
  pauseListTitle: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#8a96b0',
    marginBottom: '12px',
  },
  pauseTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
  },
  pauseTh: {
    background: '#0D2461',
    color: '#F5A800',
    padding: '8px 12px',
    textAlign: 'left',
    fontSize: '10px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  pauseTd: {
    padding: '9px 12px',
    color: '#5a6a8a',
  },
};
